/**
 * Client-side registry of query-shaping session profiles.
 *
 * A "session profile" identifies the host use case of the combined AI assistant
 * (AccentQueryShapingAssistantDlg): workflow triggers, custom validation, and future
 * hosts such as reports or dashboard widgets. The profile KEY is a shared contract with
 * the server — it is sent as `sessionProfile` on the chat request and must match a key in
 * QueryShapingSessionProfiles.All (C#), where the server-side half of the profile lives as
 * a plugin class implementing IQueryShapingSessionProfile (prompt context roots, host
 * rules, examples, result binding, and how shapes are tested).
 *
 * Responsibility split:
 *  - SERVER profile: everything the AI is told (available ctx roots, host rules, examples)
 *    plus host-specific behavior (shape testing harness, result binding, sanitization).
 *  - CLIENT profile (this file): how the dialog behaves for that host — which artifact
 *    kind outputs are tested as, whether the user may add outputs, default result shape,
 *    and the labels/hints shown around the outputs panel.
 *  - HOST props: per-instance data only (the query text, existing outputs, catalogs,
 *    variables, template values, and the result binding path when it varies per host —
 *    e.g. batch timers bind 'trigger.rows' while entity timers deliberately bind nothing).
 *
 * ADDING A PROFILE: add the server plugin first (implement IQueryShapingSessionProfile —
 * usually via QueryShapingSessionProfileBase — and register it in
 * QueryShapingSessionProfiles.All; its invariant tests verify the prompt context against
 * the shared JS harness), then
 * register the client behavior here. Hosts should configure the dialog with
 * `sessionProfile="<key>"` plus data props — never by re-specifying behavior the profile
 * already owns.
 *
 * Labels/hints are stored untranslated; the dialog runs them through t() at render time.
 */

import * as React from 'react';
import { AdhocReportPreview } from './AdhocReportPreview';
import { CommissionRulePreview } from './CommissionRulePreview';
import { DataExportPreview } from './DataExportPreview';
import { DataGridPreview } from './DataGridPreview';
import { PdfDocumentPreview } from './PdfDocumentPreview';
import { SupplyOrderIntegrationPreview } from './SupplyOrderIntegrationPreview';
import { HOST_LANES } from '../customizations/supplyOrderIntegrationDefinition';
import { HOST_LANES as PRODUCT_MODEL_HOST_LANES } from '../customizations/productModel/productModelLanes';
import { WorkflowCardPreview } from './WorkflowCardPreview';
import { getAccess, getUser } from '../../services/UserService';

// The product-model preview carries the model runtime and the 3D viewer: loaded the first time the tab renders, not
// with the registry (which every page loads).
const LazyProductModelPreview = React.lazy(() => import('../customizations/productModel/ProductModelPreview').then((m) => ({ default: m.ProductModelPreview })));
const ProductModelPreview = (props) => (
  <React.Suspense fallback={null}>
    <LazyProductModelPreview {...props} />
  </React.Suspense>
);

export const QUERY_SHAPING_DEFAULT_PROFILE = 'workflowTrigger';

export const queryShapingSessionProfiles = {
  workflowTrigger: {
    key: 'workflowTrigger',
    /** Trigger queries are SQL over the Api_* views; sent on every test request. */
    queryLanguage: 'sql',
    /** Outputs are trigger output variables, tested via the set-variable harness. */
    shapeKind: 'triggerOutput',
    /** Users (and the AI) may introduce additional output variables. */
    allowAddOutputs: true,
    /** Trigger queries are used as-is; the engine consumes the raw response. */
    resultShape: 'raw',
    /** No profile-level binding: each trigger host declares its own (or none). */
    resultBinding: '',
    outputsLabel: 'Output Variables',
    outputsHint: '',
    /**
     * Label of the composer's explicit change-intent button (sent as promptIntent
     * 'change'); the companion 'Ask a Question' button sends 'ask'. Explicit intent
     * skips the AI's "did you mean a question or a change?" clarifying round, and 'ask'
     * turns are server-enforced to never modify artifacts.
     */
    changeActionLabel: 'Update Outputs',
    /**
     * Props the host editor is expected to provide (mirrors the server profile's
     * ContextSlots). The dialog checks these on open and console-warns when a host
     * stops passing one — the client half of the context contract.
     */
    expectedContext: ['javascriptTemplateCatalog', 'runtimeNotes'],
  },
  customValidation: {
    key: 'customValidation',
    /** Validation queries are SQL over the Api_* views; sent on every test request. */
    queryLanguage: 'sql',
    /** The validation expression is a boolean expression, tested via the expression harness. */
    shapeKind: 'expression',
    /** Custom validation has exactly ONE artifact: the validation expression. */
    allowAddOutputs: false,
    /** The validation query result is materialised as an array of rows. */
    resultShape: 'all',
    /** The executed query result is bound at ctx.query for the expression. */
    resultBinding: 'query',
    outputsLabel: 'Validation expression',
    outputsHint: 'Return true when the record is VALID (the validation passes). Return false to block the stage and show the validation message.',
    changeActionLabel: 'Update Expression',
    /** Polarity/runtime guidance lives in the server profile prompt; no runtimeNotes expected. */
    expectedContext: ['javascriptTemplateCatalog'],
  },
  setVariableFromQuery: {
    key: 'setVariableFromQuery',
    /** Node queries are SQL over the Api_* views; sent on every test request. */
    queryLanguage: 'sql',
    /** The value expression runs in the engine's set-variable harness. */
    shapeKind: 'setVariable',
    /** Exactly ONE artifact besides the queries: the variable value expression. */
    allowAddOutputs: false,
    /** Query results are materialised as arrays of rows. */
    resultShape: 'all',
    /**
     * No profile-level binding: each query binds its own rows at queries.<queryName>
     * (the host derives the path via setVariableFromQueryBindingPath below).
     */
    resultBinding: '',
    /** Users may add, rename, and remove named queries in the session. */
    allowAddQueries: true,
    outputsLabel: 'Variable value expression',
    outputsHint: 'A JavaScript expression that shapes the query results into the variable value. Each query\u2019s rows are available at ctx.queries.<queryName>.',
    changeActionLabel: 'Update Expression',
    expectedContext: ['javascriptTemplateCatalog', 'runtimeNotes'],
  },
  workflowWait: {
    key: 'workflowWait',
    /** Wait queries are SQL over the Api_* views; sent on every test request. */
    queryLanguage: 'sql',
    /** The wait expression is a boolean expression, tested via the expression harness. */
    shapeKind: 'expression',
    /** A wait has exactly ONE artifact besides the query: the wait expression. */
    allowAddOutputs: false,
    /** Default result shape; the host overrides per wait with its configured mode. */
    resultShape: 'all',
    /** The executed query result is bound at ctx.query for the expression. */
    resultBinding: 'query',
    outputsLabel: 'Wait expression',
    outputsHint: 'Return true when the wait condition is MET (the workflow continues). Return false to keep waiting; the engine re-evaluates on its next poll.',
    changeActionLabel: 'Update Expression',
    expectedContext: ['javascriptTemplateCatalog', 'runtimeNotes'],
  },
  workflowNodeQuery: {
    key: 'workflowNodeQuery',
    /** Node queries are SQL over the Api_* views; sent on every test request. */
    queryLanguage: 'sql',
    /**
     * QUERY-ONLY host: the runtime consumes the query result directly (guard truthiness,
     * first row's ID, recipient rows, setting value) with no JavaScript output. The
     * dialog hides the outputs panel and the session authors only the query.
     */
    hasOutputs: false,
    allowAddOutputs: false,
    /** Query results are materialised as arrays of rows for previews. */
    resultShape: 'all',
    resultBinding: '',
    outputsLabel: '',
    outputsHint: '',
    okLabel: 'Use Query',
    chatPlaceholder: 'Describe the rows this query should return...',
    changeActionLabel: 'Update Query',
    /** The host editor MUST state how its runtime consumes the result (runtimeNotes). */
    expectedContext: ['runtimeNotes'],
  },
  adhocReport: {
    key: 'adhocReport',
    /**
     * Output tests run through the CUSTOMIZATION endpoints (web app in-process, gated
     * on customization management) instead of the workflow node-test endpoints —
     * customization authoring must not require the Workflow feature or the workflow
     * engine service to exist.
     */
    nodeTestScope: 'customization',
    /** Report queries are SQL over the Api_* views; sent on every test/refresh request. */
    queryLanguage: 'sql',
    /** The report script evaluates like a set-variable value (any JSON value; here a string). */
    shapeKind: 'setVariable',
    /** Exactly ONE artifact besides the queries: the report HTML script. */
    allowAddOutputs: false,
    /** Query results are materialised as arrays of rows. */
    resultShape: 'all',
    /** Each query binds its own rows at queries.<queryName> (see bindingPath below). */
    resultBinding: '',
    /** Users (and the AI) may plan multi-query reports. */
    allowAddQueries: true,
    outputsLabel: 'Report script',
    outputsHint: 'A JavaScript expression that renders the query results into one complete standalone HTML document (a string). Each query\u2019s rows are available at ctx.queries.<queryName>.',
    /** Standalone host (toolbar): there is no editor behind it to supply catalogs/notes. */
    expectedContext: [],
    /** The report's design references (a mock-up, KPI definitions) ride the dependentAssets lane; see DependentAssetsCtrl. */
    supportsDependentAssets: true,
    /**
     * Profile-owned preview rendered in the dialog's Preview tab: the sandboxed report
     * iframe plus the third-party-libraries disclaimer/consent. Profiles without a
     * previewComponent get DefaultQueryShapingPreview (generic tables/values).
     * The component receives { queries, outputs, busy, sessionOptions, templateValues,
     * onTemplateValuesChange, onSessionOptionsChange, onRefresh, onDefect }; onRefresh
     * accepts an optional template-values patch that the refresh run merges in (the
     * report date-range picker's computed instants).
     */
    previewComponent: AdhocReportPreview,
    /**
     * No preview snip (AccentRegionSnip): the report renders in an opaque-origin sandboxed
     * iframe, so the parent document neither receives the drag nor can rasterise the frame
     * (html2canvas cannot read into it). Every other profile supports the snip by default.
     */
    supportsSnip: false,
    /** Reports are consumed visually, so the preview is the tab users land on. */
    defaultTab: 'preview',
    dialogTitle: 'Report Assistant',
    /** Standalone session: nothing is persisted on close, so the OK action just closes. */
    okLabel: 'Done',
    chatPlaceholder: 'Describe the report you want (e.g. "top 10 customers by sales amount as a table and pie chart")...',
    changeActionLabel: 'Update Report',
  },
  dataExport: {
    key: 'dataExport',
    /** Output tests run through the customization endpoints (see adhocReport). */
    nodeTestScope: 'customization',
    /** Sheet queries are SQL over the Api_* views; sent on every test/refresh request. */
    queryLanguage: 'sql',
    /** Mapping scripts evaluate like set-variable values (here an array of row objects). */
    shapeKind: 'setVariable',
    /**
     * Each query (sheet) pairs with ONE same-named mapping output, so adding a sheet
     * means the AI adds a query AND an output — both add capabilities are on.
     */
    allowAddOutputs: true,
    /** Query results are materialised as arrays of rows. */
    resultShape: 'all',
    /** Each query binds its own rows at queries.<queryName> (see bindingPath below). */
    resultBinding: '',
    allowAddQueries: true,
    /**
     * $top is SEMANTIC here (the sheet's row limit, honored by the generated file), so
     * an all-rows sheet has NO $top — the dialog's refresh instead asks the test
     * endpoint to clamp rows for the preview. Mirrors DataExportSessionProfile.MaxPreviewRows (C#).
     */
    previewMaxRows: 200,
    outputsLabel: 'Sheet column mappings',
    outputsHint: 'One mapping per sheet, named after its query: a JavaScript expression that turns ctx.queries.<name> rows into an array of flat objects (keys = column headers, values = cell values).',
    /** Standalone host (toolbar): there is no editor behind it to supply catalogs/notes. */
    expectedContext: [],
    /** The export's design references (the import layout it must match, a sample workbook as CSV) ride the dependentAssets lane. */
    supportsDependentAssets: true,
    /**
     * Profile-owned preview: one tab per sheet showing the mapped rows as a table, plus
     * the Generate Export button that posts the definition snapshot to the start-export
     * endpoint (a background task delivers the workbook via a download notification).
     */
    previewComponent: DataExportPreview,
    defaultTab: 'preview',
    dialogTitle: 'Data Export Assistant',
    /** Standalone session: nothing is persisted on close, so the OK action just closes. */
    okLabel: 'Done',
    chatPlaceholder: 'Describe the export you want (e.g. "all open jobs with customer, sales rep and amount, plus a payments sheet")...',
    changeActionLabel: 'Update Export',
  },
  dataGrid: {
    key: 'dataGrid',
    /** Output tests run through the customization endpoints (see adhocReport). */
    nodeTestScope: 'customization',
    /** Grid queries are SQL over the Api_* views; sent on every test/page/refresh request. */
    queryLanguage: 'sql',
    /** The grid definition evaluates like a set-variable value (a static JSON object). */
    shapeKind: 'setVariable',
    /** Exactly ONE artifact besides the query: the 'grid' definition output. */
    allowAddOutputs: false,
    /** Query results are materialised as arrays of rows. */
    resultShape: 'all',
    /** Each query binds its own rows at queries.<queryName> (see bindingPath below). */
    resultBinding: '',
    /**
     * The AI may add CHILD queries beyond the fixed 'data' grid query: each backs a
     * { queryName } child grid, filtered per expanded row via {{parent.<Field>}}
     * tokens (how an aggregate main grid still expands into detail rows).
     */
    allowAddQueries: true,
    /**
     * TOP is SEMANTIC here (the grid's total-row cap — the preview pages with the
     * host-composed grid state), so session test runs clamp rows separately. Mirrors
     * DataGridSessionProfile.MaxPreviewRows (C#).
     */
    previewMaxRows: 50,
    outputsLabel: 'Grid definition',
    outputsHint: 'A static object declaring the grid\u2019s columns ({ field, title, picklist?, link?, align?, style?, cellStyles? }) and optional child grids ({ queryName, title, columns, childGrids? } — child grids nest) — every field is a real returned column name so paging and sorting can re-run the query.',
    /** Standalone host (toolbar): there is no editor behind it to supply catalogs/notes. */
    expectedContext: [],
    /** The grid's design references (the spreadsheet it replaces, a column brief) ride the dependentAssets lane. */
    supportsDependentAssets: true,
    /**
     * Profile-owned preview: the query's rows rendered live in a paged, sortable HTML
     * grid (grid state is structured — header clicks become gridState.sort, the pager
     * moves gridState.skip; the server composes them around the SQL), with expandable
     * query-backed child grids.
     */
    previewComponent: DataGridPreview,
    /** Grids are consumed visually, so the preview is the tab users land on. */
    defaultTab: 'preview',
    dialogTitle: 'Data Grid Assistant',
    /** Standalone session: nothing is persisted on close, so the OK action just closes. */
    okLabel: 'Done',
    chatPlaceholder: 'Describe the data you want to browse (e.g. "all open jobs with customer and amount, with their job lines")...',
    changeActionLabel: 'Update Grid',
  },
  workflowCard: {
    key: 'workflowCard',
    /**
     * WORKFLOW BOARD CARD AUTHORING (ManageWorkflowCardsView): the session authors the
     * card's queries plus ONE 'render' output — a JavaScript expression that evaluates
     * to the card's inner HTML fragment for one entity. Output tests run through the
     * workflow node-test endpoints (cards live inside workflow management). Server
     * half: WorkflowCardSessionProfile (C#), which tests the script once per stage of
     * every workflow using the card (from hostContextJson) plus an unknown stage.
     */
    queryLanguage: 'sql',
    /** The render script evaluates like a set-variable value (here an HTML string). */
    shapeKind: 'setVariable',
    /** Exactly ONE artifact besides the queries: the 'render' script. */
    allowAddOutputs: false,
    /** Query results are materialised as arrays of rows. */
    resultShape: 'all',
    /** Each query binds its own rows at queries.<queryName> (see bindingPath below). */
    resultBinding: '',
    /**
     * Usually one query; the AI may add a second for a child list the card renders row
     * by row. Every query returns rows for ALL records keyed by EntityID — the board
     * restricts them to the visible cards itself, so TOP is forbidden and session test
     * runs clamp rows here instead. Mirrors WorkflowCardSessionProfile.MaxPreviewRows (C#).
     */
    allowAddQueries: true,
    previewMaxRows: 50,
    outputsLabel: 'Card render script',
    outputsHint: 'A JavaScript expression that returns the card\u2019s inner HTML (a string fragment) for one entity. This card\u2019s rows are at ctx.queries.<queryName>, the card facts at ctx.card (stageKey, stageName, workflowName, title…) and format helpers at ctx.fmt (esc, formatDate, formatCurrency…). Branch on ctx.card.stageKey for stage-specific designs, always with a default.',
    /** The designer must send the host context (entity type + workflows/stages using the card). */
    expectedContext: ['hostContextJson'],
    /**
     * Profile-owned preview: a workflow selector (every workflow using the card), a test
     * entity picker, and the card rendered for EVERY stage of that workflow at once plus
     * an "Other stages" card (the script's default branch) — all for the same entity.
     */
    previewComponent: WorkflowCardPreview,
    /** Cards are consumed visually, so the preview is the tab users land on. */
    defaultTab: 'preview',
    dialogTitle: 'Workflow Card Assistant',
    okLabel: 'Use Card',
    chatPlaceholder: 'Describe the card (e.g. "customer and job reference, the total, and the install date once the job is scheduled")...',
    changeActionLabel: 'Update Card',
  },
  productChangeReview: {
    key: 'productChangeReview',
    /**
     * CHAT-ONLY profile: the session reviews a product definition change — it authors
     * no queries and no scripts, so the dialog hides both the outputs panel and the
     * query editor (hosts pass previewContent to show the Changes screen instead).
     * Server half: ChangeReviewSessionProfile (C#) with the consultProductSpec skill,
     * grounded by the ProductSpecificationOracle on the generated ProductSpec/
     * PricingSpec documents plus the saved definition (loaded server-side by
     * changeProductID — the client never uploads it).
     */
    hasQueries: false,
    hasOutputs: false,
    allowAddOutputs: false,
    resultShape: 'raw',
    resultBinding: '',
    outputsLabel: '',
    outputsHint: '',
    dialogTitle: 'Product Change Review',
    /**
     * The session's deliverable is the REVIEW CHECKLIST artifact, grouped by LOGICAL
     * CHANGE (related edits serving one editing goal, e.g. creating an option and
     * setting it as a pricing option). Its content is produced server-side from the
     * product specification oracle's assessment (server lane
     * ChecklistSessionArtifactLane, merged by item id) — the user ticks each item
     * Intended/Unintended in the dialog's checklist tab. checklistJson round-trips on
     * every request; user states are preserved server-side across re-assessments.
     */
    hasChecklist: true,
    /**
     * Sent automatically as the session's first turn (intent 'change') when the dialog
     * opens fresh with a diff present, so the review is underway before the user has
     * to ask for anything: the assistant consults the oracle, whose logical-change
     * assessment becomes the checklist in that same turn. Resumed sessions never
     * re-send it.
     */
    autoFirstPrompt: 'Review this change and build the checklist of its effects.',
    /** Review sessions change nothing; closing just dismisses the conversation. */
    okLabel: 'Done',
    chatPlaceholder: 'State what this change is meant to achieve, or ask about its effects...',
    changeActionLabel: 'Review Change',
    /**
     * The host dialog (product editor Changes dialog) must provide the computed diff of
     * the edit (changeDiffJson, DiffViewer path grammar) and the product id
     * (productID); the intent is typed by the user in chat. Mirrors the server
     * profile's ContextSlots.
     */
    expectedContext: ['changeDiffJson', 'productID'],
  },
  productExplainer: {
    key: 'productExplainer',
    /**
     * CHAT-ONLY profile: diagnostic questions about a configured job line ("why can't
     * I see option X when I select Y?", "why is option Z priced at $20?"). Hosted by
     * the job line editor (JobLineView) and the price details line rows
     * (JobPriceDetailsCtrl) via the AI wand; both snapshot the line(s) — identity by
     * name (product name/code/version, supplier, multi-config membership),
     * dimensions, chosen options and PriceModel — as explainContextJson (see
     * buildJobLineExplainContext), and the server loads the actual definitions by the
     * ids inside it. Server half: ProductExplainerSessionProfile (C#) with the
     * consultProductSpec skill.
     */
    hasQueries: false,
    hasOutputs: false,
    allowAddOutputs: false,
    resultShape: 'raw',
    resultBinding: '',
    outputsLabel: '',
    outputsHint: '',
    dialogTitle: 'Product & Pricing Assistant',
    /** Explainer sessions change nothing; closing just dismisses the conversation. */
    okLabel: 'Done',
    chatPlaceholder: 'Ask about this configuration (e.g. "why can\u2019t I see option X when I select Y?" or "why is option Z priced at $20?")...',
    /** The host must snapshot the line configuration being asked about. */
    expectedContext: ['explainContextJson'],
  },
  commissionRule: {
    key: 'commissionRule',
    /**
     * COMMISSION RULE AUTHORING: the session authors ONE rule (the pre-seeded 'rule'
     * output) plus the commission VALUES it needs ('value:<Name>' outputs — new values
     * or full replacements of existing ones, filters included). Outputs are JSON
     * artifacts in the Flee shape language, not JavaScript; there are no queries — the
     * whole commission plan (every rule and value, identity by name) travels as
     * hostContextJson and is the assistant's data. Server half:
     * CommissionRuleSessionProfile + CommissionAuthoringService (C#): compile-checks
     * every artifact against the merged value set, validates filters structurally,
     * checks tenant-id provenance and rejects users/removals.
     */
    hasQueries: false,
    /** Artifact tests run in the web app via the commissionRule host adapter. */
    nodeTestScope: 'customization',
    shapeKind: 'commissionArtifact',
    /** The editors show JSON, not JavaScript, and offer no per-output test button. */
    outputLanguage: 'json',
    hideOutputTest: true,
    /**
     * Users do not add or rename outputs by hand (the names are the contract), but the
     * ASSISTANT introduces 'value:<Name>' outputs whenever the rule needs a new value.
     */
    allowAddOutputs: false,
    aiMayAddOutputs: true,
    resultShape: 'raw',
    resultBinding: '',
    outputsLabel: 'Rule and value artifacts',
    outputsHint: 'The focused rule ("rule") and each value the assistant adds or changes ("value:<Name>") as JSON. Review them in the Preview tab; they are applied to the commission plan when you click Apply.',
    /**
     * PLAN-FIRST: the assistant must ship a plan (lookups, values, the rule, checks)
     * that the user approves in the Plan panel before any artifact is accepted (server
     * lane PlanSessionArtifactLane). planJson/planApproved round-trip on every request.
     */
    hasPlan: true,
    /** Commission plans (the signed agreement, the rate card) ride the dependentAssets lane. */
    supportsDependentAssets: true,
    previewComponent: CommissionRulePreview,
    defaultTab: 'preview',
    dialogTitle: 'Commission Rule Assistant',
    okLabel: 'Apply to plan',
    chatPlaceholder: 'Describe the commission rule you want (e.g. "10% of roller blind sales on cooling-off, paid when the job is paid in full")...',
    changeActionLabel: 'Author Rule',
    /**
     * The commissions editor must send the whole plan as host context, and the prompt
     * catalog (products + the filter schema's lookups) that gives the right-click menu
     * and pre-resolved tenant ids.
     */
    expectedContext: ['hostContextJson', 'templateCatalog'],
  },
  pdfDocument: {
    key: 'pdfDocument',
    /**
     * PDF DOCUMENT TEMPLATE AUTHORING: the session authors the 'document' output — a
     * pdfscript function body (JavaScript over ctx.data + the P.* builder, ending in
     * return P.page(...)) that the document engine renders to PDF — plus the template's
     * print-time SQL queries (session queries, up to PdfDocumentDefinition.MaxQueries,
     * each scoped by a {{record token}} such as {{job.ID}} and read as
     * ctx.queries.<name>) and its print-time parameter declarations (documentParamsJson,
     * server lane DocumentParamsSessionArtifactLane, read as ctx.params.<name>). The
     * editor (PdfDocumentEditorDlg) passes the stored queries as existingQueries, the
     * record tokens as templateCatalog + initialTemplateValues, the parameters as
     * initialDocumentParamsJson, and the test job, print setting, signing mode and the
     * definition WITHOUT those artifacts as hostContextJson; OK returns outputs,
     * queries and documentParams. Server half: PdfDocumentSessionProfile +
     * PdfDocumentAuthoringService (C#): adds the test job's actual ctx.data (session
     * queries included) and renders every candidate through the real preview path.
     */
    /** Artifact tests run in the web app via the pdfDocument host adapter. */
    nodeTestScope: 'customization',
    queryLanguage: 'sql',
    shapeKind: 'pdfDocument',
    /**
     * Query rows are read whole (ctx.queries.<name> is the row array), the binding is
     * derived from the query name, and the user/assistant may add or remove queries.
     */
    resultShape: 'all',
    allowAddQueries: true,
    /** The assistant authors the print-time parameter declarations (artifact: params). */
    hasDocumentParams: true,
    /**
     * The template's dependent assets (dependentAssets lane): design references the assistant reads (a brand
     * guide, a sample document) and - because the engine embeds assets at run time - runtime ones too.
     */
    supportsDependentAssets: true,
    dependentAssetsAllowRuntime: true,
    /**
     * The script is a function body for the pdfscript engine, not an expression for the
     * shared JS harness: no client-side output tests at all (the server renders each
     * candidate during the turn; the Preview tab renders the user's copy).
     */
    hideOutputTest: true,
    clientOutputTests: false,
    allowAddOutputs: false,
    aiMayAddOutputs: false,
    resultBinding: '',
    outputsLabel: 'Document script',
    outputsHint: 'The pdfscript function body of the template (ends in return P.page(...)). Review the rendered PDF in the Preview tab; "Use Script" puts the script, the queries and the parameters in the editor, where you save and preview as usual.',
    previewComponent: PdfDocumentPreview,
    defaultTab: 'preview',
    dialogTitle: 'Document Template Assistant',
    okLabel: 'Use Script',
    // type-neutral: the same assistant designs quotes, order confirmations and invoices (the editor's suggested prompts are per type)
    chatPlaceholder: 'Describe the document you want (e.g. "one page, logo top-left, lines grouped by room and the totals boxed bottom-right")...',
    changeActionLabel: 'Update Document',
    /**
     * The editor must send the template / test-job context (the data itself is added
     * server-side) and the record-token catalog the queries may be scoped by.
     */
    expectedContext: ['hostContextJson', 'templateCatalog'],
  },
  supplyOrderIntegration: {
    key: 'supplyOrderIntegration',
    /**
     * SUPPLIER ORDER INTEGRATION AUTHORING: the session authors the five fixed outputs of a
     * supplyOrderIntegration customisation - 'dataStore' (the data store rows the order
     * needs, read as ctx.dataStore["scope:key"] by the others), 'transports' (where orders
     * go), 'credentials' (how each transport authenticates), 'dispatch' (the order file(s)
     * and their routes) and 'orderResponse' (how the supplier's reply is read) - plus the
     * integration's SQL queries (session queries, read as ctx.queries.<name>) and the host
     * lane the assistant writes beside the scripts: the recorded sample replies (artifact:
     * samples) orderResponse is tested against. The editor (SupplyOrderIntegrationEditorDlg)
     * passes the stored outputs, queries, the lane artifacts as initialLaneArtifacts, the
     * supply-job record tokens as templateCatalog + initialTemplateValues, and the test
     * supply job / definition WITHOUT those artifacts as hostContextJson; OK returns
     * outputs, queries and laneArtifacts. Server half: SupplyOrderIntegrationSessionProfile
     * + SupplyOrderIntegrationAuthoringService (C#): adds the test supply job's ctx.data,
     * the supplier's facts (accounts, the names of existing data store rows) and runs every
     * candidate through the real runtime with the dataStore requests' samples (never the
     * tenant's store, never a secret value). One shape among many: the same assistant
     * designs JSON posts, SFTP drops and e-mailed files - nothing here is supplier-specific.
     */
    /** Artifact tests run in the web app via the supplyOrderIntegration host adapter. */
    nodeTestScope: 'customization',
    queryLanguage: 'sql',
    shapeKind: 'supplyOrderIntegration',
    /** Query rows are read whole (ctx.queries.<name> is the row array); the user/assistant may add or remove queries. */
    resultShape: 'all',
    allowAddQueries: true,
    /** The host lanes the dialog round-trips (laneArtifacts) and shows in the transcript when the turn changes them. */
    hostLanes: HOST_LANES.map(({ key, label }) => ({ key, label })),
    /** The supplier's documentation (API manual, field spreadsheet, Swagger) rides the dependentAssets lane instead of being re-pasted. */
    supportsDependentAssets: true,
    /**
     * The scripts are function bodies for the supply order script engine, not expressions for
     * the shared JS harness: no client-side output tests (the server runs each candidate
     * during the turn; the Preview tab runs the user's copy).
     */
    hideOutputTest: true,
    clientOutputTests: false,
    /** The five outputs are fixed by the contract: the editor passes all of them, nobody adds a sixth. */
    allowAddOutputs: false,
    aiMayAddOutputs: false,
    resultBinding: '',
    outputsLabel: 'Integration scripts',
    outputsHint: 'dataStore, transports, credentials, dispatch and orderResponse - the five scripts of the integration. Review the produced files, the send plan and the sample replies in the Preview tab; "Use Scripts" puts the scripts, the queries and the samples in the editor, where you save, enter the data store rows and publish as usual.',
    /** Each script is a function body for the supply order script engine (ends in return { ... }), not a single expression. */
    outputScriptLabel: 'JavaScript function body',
    /** One line per fixed output, shown as the heading of its box so the five scripts are told apart. */
    outputDescriptions: {
      dataStore: 'The data store rows this order needs ([{ scope, key, description, sample, optional }]), computed from the order; the other scripts read them as ctx.dataStore["scope:key"]. Empty when nothing is read from the data store.',
      transports: 'Where orders go: the named transports (webEndpoint, sftp or email) and how each is reached. No order data, no secrets.',
      credentials: 'How each transport authenticates, built from the requested data store rows (ctx.dataStore - an encrypted row is a $secret{scope:key} token the sender resolves) and the supplier. Empty when no transport names an auth (e.g. email only).',
      dispatch: 'The order file(s) built from the supply job (ctx.data, ctx.queries) and the transport each one routes to.',
      orderResponse: 'How the supplier\u2019s reply (ctx.response) is read: accepted, supplierOrderNumber, messages. Empty when no transport is a web endpoint.',
    },
    previewComponent: SupplyOrderIntegrationPreview,
    defaultTab: 'preview',
    dialogTitle: 'Supplier Integration Assistant',
    okLabel: 'Use Scripts',
    chatPlaceholder: 'Describe how this supplier receives orders (e.g. "one JSON order per POST to their orders endpoint with a bearer token per account, reply carries their order number")...',
    /**
     * The session asks for the supplier's documentation to be PASTED (request schemas,
     * sample replies); the composer's prose default would cut those off. The server
     * profile's TranscriptBudget is raised to match so the paste survives into later turns.
     */
    promptMaxChars: 40000,
    changeActionLabel: 'Update Integration',
    /**
     * The editor must send the test supply job / definition context (the order data itself
     * is added server-side) and the record-token catalog the queries may be scoped by.
     */
    expectedContext: ['hostContextJson', 'templateCatalog'],
  },
  productModel: {
    key: 'productModel',
    /**
     * PRODUCT MODEL AUTHORING (product visualisation, RTL_ProductVisualization): the session authors ONE output -
     * 'model', a ModelScript function body (ctx + the M builder, return M.group([...])) the BROWSER runs in a Web Worker
     * to build a 3D model of a blind / shutter / curtain for any opening, fit and state - plus two host lanes: the
     * parameters the script reads (artifact: modelParams) and the render samples it is proved at (artifact:
     * modelSamples). No queries: the editor's host context (the look, the last browser run's facts, templates while
     * blank) is the data. Server half: ProductModelSessionProfile (C#) PARSES each candidate only; the Preview tab
     * (ProductModelPreview) runs every sample in the browser after the turn and posts an AUTOMATIC RUN REPORT back into
     * the chat when one fails (at most 8 in a row), so run-time repair is an ordinary turn.
     */
    hasQueries: false,
    nodeTestScope: 'customization',
    shapeKind: 'productModel',
    hostLanes: PRODUCT_MODEL_HOST_LANES.map(({ key, label }) => ({ key, label })),
    /** Brochures, spec sheets and product photos ride the dependentAssets lane. */
    supportsDependentAssets: true,
    /** The script is a function body for the browser runtime, not an expression for the shared JS harness. */
    hideOutputTest: true,
    clientOutputTests: false,
    allowAddOutputs: false,
    aiMayAddOutputs: false,
    resultShape: 'raw',
    resultBinding: '',
    outputsLabel: 'Model script',
    outputsHint: 'The ModelScript function body (ends in return M.group([...])). The Preview tab runs it at every sample in the 3D viewer; "Use Script" puts the script, the parameters and the samples in the editor, where you save and publish as usual.',
    outputScriptLabel: 'JavaScript function body',
    previewComponent: ProductModelPreview,
    defaultTab: 'preview',
    dialogTitle: 'Product Model Assistant',
    okLabel: 'Use Script',
    chatPlaceholder: 'Describe the product (e.g. "a roller blind with a cassette and a 40 mm tube", "a plantation shutter with 89 mm louvres")...',
    changeActionLabel: 'Update Model',
    /** A pasted spec sheet must arrive whole. */
    promptMaxChars: 20000,
    expectedContext: ['hostContextJson'],
  },
};

/**
 * User-feature gate for the product explainer wand (productExplainer) on the job line
 * editor (JobLineView) and the price details line rows (JobPriceDetailsCtrl) — the
 * change review (productChangeReview) is generally released and does not check this.
 * Granted per representative via FeatureAllowProductExplainer (Representative editor →
 * features); Insyte support users always pass, mirroring the server. Presentation-only:
 * the server re-checks the same feature on every productExplainer chat turn
 * (Workflow_QueryShaping_ChatStart).
 */
export const productExplainerEnabled = () => {
  try {
    const access = getAccess();
    if (!access) return false;
    if (getUser()?.IsSupportUser) return true;
    return !!access.features?.().FeatureAllowProductExplainer?.();
  } catch {
    return false;
  }
};

/**
 * The ctx path a Set Variable (Query) query's rows are bound at. Mirrors
 * SetVariableFromQueryQueries.BindingPath (C#) so dialog tests, engine tests, and run-time
 * execution all bind identically.
 */
export const setVariableFromQueryBindingPath = (queryName) => `queries.${(queryName ?? '').toString().trim()}`;

/**
 * Resolves a profile by key, falling back to the default profile for unknown keys —
 * mirroring the server's ResolveSessionProfile so client and server always degrade the
 * same way.
 */
export const resolveQueryShapingProfile = (key) => (
  queryShapingSessionProfiles[key] ?? queryShapingSessionProfiles[QUERY_SHAPING_DEFAULT_PROFILE]
);
